<template>
  <div class="space-y-2">
    <div 
      v-for="item in reactiveItems" 
      :key="item.id"
      class="bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-200 overflow-hidden touch-manipulation active:scale-98"
    >
      <div 
        @click="handleItemClick(item)"
        class="flex items-center p-4 cursor-pointer relative"
      >
        <!-- Icon -->
        <div class="relative flex-shrink-0 mr-4">
          <div class="w-12 h-12 rounded-xl flex items-center justify-center"
               :class="item.type === 'folder' ? 'bg-amber-50' : 'bg-blue-50'">
            <i :class="getIconClass(item)" class="text-xl"></i>
          </div>
          
          <!-- Folder expand indicator -->
          <div v-if="item.type === 'folder'" class="absolute -bottom-1 -right-1">
            <div v-if="item.loading" class="w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center">
              <div class="animate-spin rounded-full h-3 w-3 border-2 border-blue-500 border-t-transparent"></div>
            </div>
            <div v-else class="w-6 h-6 bg-gray-100 rounded-full shadow-md flex items-center justify-center">
              <i :class="item.expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
                 class="text-gray-600 text-xs"></i>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <!-- Name -->
          <div class="font-medium text-gray-900 text-lg truncate mb-1">
            {{ item.name }}
          </div>
          
          <!-- Metadata -->
          <div class="flex items-center text-sm text-gray-500 space-x-3">
            <span v-if="item.type === 'file' && item.size">
              {{ formatFileSize(item.size) }}
            </span>
            
            <span v-if="item.type === 'file'">
              Click to preview
            </span>
            
            <span v-if="item.type === 'folder'">
              {{ item.expanded ? 'Tap to collapse' : 'Tap to expand' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Nested folders -->
      <div v-if="item.type === 'folder' && item.expanded && item.children" 
           class="ml-8 border-l-2 border-gray-100 pl-4 mt-2 pb-4">
        <PublicFolderTree 
          :items="item.children" 
          :id="id"
          @message="$emit('message', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { PublicApiService } from '../services/publicApiService'

interface FolderTreeItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  children?: FolderTreeItem[]
  expanded?: boolean
  loading?: boolean
  childrenLoaded?: boolean
}

const props = defineProps<{
  items: FolderTreeItem[]
  id: string
  onMessage?: (text: string) => void
}>()

const emit = defineEmits<{
  message: [text: string]
}>()

const router = useRouter()

// Make items reactive within this component
const reactiveItems = ref<FolderTreeItem[]>([])

// Track which file is being downloaded
const downloading = ref<string | null>(null)

// Watch for changes in props.items and update reactive items
watch(() => props.items, (newItems) => {
  reactiveItems.value = [...newItems]
}, { immediate: true, deep: true })

// Handle item click - unified behavior for files and folders
function handleItemClick(item: FolderTreeItem): void {
  if (item.type === 'folder') {
    toggleFolder(item)
  } else {
    // Navigate to file preview
    viewFile(item)
  }
}

function toggleFolder(folder: FolderTreeItem): void {
  // Since children are already loaded from the parent structure, just toggle the expanded state
  folder.expanded = !folder.expanded
  console.log(`Folder ${folder.name} expanded state:`, folder.expanded)
}

function getIconClass(item: FolderTreeItem): string {
  if (item.type === 'folder') {
    return item.expanded ? 'fas fa-folder-open text-amber-500' : 'fas fa-folder text-amber-600'
  }
  
  // File type icons based on mime type or extension
  const mimeType = item.mimeType || ''
  const fileName = item.name || ''
  
  if (mimeType.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName)) {
    return 'fas fa-image text-emerald-500'
  }
  if (mimeType.includes('video') || /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(fileName)) {
    return 'fas fa-video text-rose-500'
  }
  if (mimeType.includes('audio') || /\.(mp3|wav|flac|aac|ogg)$/i.test(fileName)) {
    return 'fas fa-music text-purple-500'
  }
  if (mimeType.includes('pdf') || /\.pdf$/i.test(fileName)) {
    return 'fas fa-file-pdf text-red-600'
  }
  if (mimeType.includes('document') || mimeType.includes('text') || /\.(doc|docx|txt|rtf)$/i.test(fileName)) {
    return 'fas fa-file-alt text-blue-500'
  }
  if (mimeType.includes('spreadsheet') || /\.(xls|xlsx|csv)$/i.test(fileName)) {
    return 'fas fa-file-excel text-green-600'
  }
  if (mimeType.includes('presentation') || /\.(ppt|pptx)$/i.test(fileName)) {
    return 'fas fa-file-powerpoint text-orange-500'
  }
  if (mimeType.includes('zip') || mimeType.includes('archive') || /\.(zip|rar|7z|tar|gz)$/i.test(fileName)) {
    return 'fas fa-file-archive text-gray-600'
  }
  
  return 'fas fa-file text-slate-500'
}

function formatFileSize(bytes: number): string {
  if (!bytes) return ''
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

function viewFile(file: FolderTreeItem): void {
  // Navigate to file preview page
  router.push(`/app/${props.id}/file/${file.id}`)
}

// Function to download file
const downloadFile = async (file: FolderTreeItem): Promise<void> => {
  if (!file.id) return
  
  try {
    downloading.value = file.id
    
    // Get download URL
    const downloadResponse = await PublicApiService.getPublicFileDownloadUrl(
      props.id, 
      file.id
    )
    
    // Create a temporary link to trigger download
    const link = document.createElement('a')
    link.href = downloadResponse.url
    link.download = downloadResponse.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    props.onMessage?.('File downloaded successfully!')
  } catch (error: any) {
    console.error('Download failed:', error)
    props.onMessage?.(`Download failed: ${error.message}`)
  } finally {
    downloading.value = null
  }
}

onMounted(async () => {
  // Component is now ready
})
</script>

<style scoped>
.active\:scale-98:active {
  transform: scale(0.98);
}

.touch-manipulation {
  touch-action: manipulation;
}
</style> 